import React, { useState, useEffect } from 'react';
import { Globe, RefreshCw, Trash2, Eye, X, PlusCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Table, Space, message } from 'antd';
import { BASE_URL } from '../../../../config/api';
import axios from 'axios';
import DomainModal from '../../../../components/Modals/Server/DomainModal';

const Domain = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { projectId, serviceId } = useParams();

  const fetchDomains = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`https://cloudify-prod.wizr.in/${projectId}/service/${serviceId}/domains`);
      setDomains(response.data.domains || []);
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError('Failed to fetch domains. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, [serviceId]);

  const handleAddDomain = async (domainData) => {
    try {
      await axios.post(`https://cloudify-prod.wizr.in/${projectId}/service/${serviceId}/domain`, domainData);
      message.success('Domain added successfully');
      fetchDomains();
    } catch (err) {
      console.error('Error adding domain:', err);
      message.error('Failed to add domain. Please try again.');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    try {
      await axios.delete(`https://cloudify-prod.wizr.in/${projectId}/service/${serviceId}/domain/${domainId}`);
      message.success('Domain deleted successfully');
      fetchDomains();
    } catch (err) {
      console.error('Error deleting domain:', err);
      message.error('Failed to delete domain. Please try again.');
    }
  };

  const columns = [
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      width: '40%',
      render: (text) => (
        <div className="flex items-center text-gray-700">
          <Globe className="h-4 w-4 mr-2 text-gray-500" />
          {text}
        </div>
      ),
    },
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path',
      width: '40%',
      render: (text) => (
        <span className="text-gray-700 font-medium">{text || 'No path to expose'}</span>
      ),
    },
    {
      title: 'Source IPs',
      dataIndex: 'sourceIP',
      key: 'sourceIP',
      width: '20%',
      render: (text) => (
        <span className="text-gray-700">{text || 'Any'}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, domain) => (
        <Space>
          <button
            onClick={() => {
              handleDeleteDomain(domain.domainId);
            }}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors duration-200"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Domains</h2>
          <p className="text-gray-500 text-sm">Manage your domain mappings</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md gap-2"
        >
          <PlusCircle className="h-5 w-5"/>
          Add Domain
        </button>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={domains}
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} domains`,
          }}
        />
      </div>
      
      {/* Domain Add Modal */}
      <DomainModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSubmit={handleAddDomain}
      />
      
    </div>
  );
};

export default Domain;